import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import AvatarGroup from '@mui/material/AvatarGroup'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined'
import LinkIcon from '@mui/icons-material/Link'
import AnDatePicker from '../../../../components/DateTime/DatePicker'
import {
  emptySlot,
  getAssigneeName,
  isSlotAssigned,
  SlotAssignmentFields,
} from '../../../events/SingleEvent/Shifts/ShiftSlotAssignments'
import {
  formatDutyRange,
  getSeatSlot,
  getSeatTimes,
  isLockedSeat,
  MAX_REPEAT_DUTIES,
  MAX_REPEAT_SHIFTS,
  repeatDayOptions,
} from './dutyRepeat'

const DAY_PRESETS = [
  { label: 'Weekdays', days: [1, 2, 3, 4, 5] },
  { label: 'Weekends', days: [0, 6] },
  { label: 'Every day', days: [0, 1, 2, 3, 4, 5, 6] },
]

const LENGTH_PRESETS = [1, 2, 4, 8, 12]

const sameDays = (a = [], b = []) =>
  a.length === b.length && a.every((day) => b.includes(day))

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    fontWeight={700}
    lineHeight={1.6}
    display="block"
  >
    {children}
  </Typography>
)

const Stat = ({ value, label, tone }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.default',
    }}
  >
    <Typography
      variant="h5"
      fontWeight={800}
      lineHeight={1.2}
      color={tone ?? 'text.primary'}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap display="block">
      {label}
    </Typography>
  </Box>
)

const SeatAvatar = ({ slot }) => {
  if (slot?.assignedToSubcontractor) {
    return (
      <Avatar sx={{ bgcolor: 'contrast.main' }}>
        <HandshakeOutlinedIcon sx={{ fontSize: 14 }} />
      </Avatar>
    )
  }
  if (isSlotAssigned(slot)) {
    const name = getAssigneeName(slot.assignedTo)
    return (
      <Tooltip title={name}>
        <Avatar
          src={
            slot.assignedTo?.photograph ||
            slot.assignedTo?.employee?.userAccount?.photograph
          }
        >
          {name.charAt(0)}
        </Avatar>
      </Tooltip>
    )
  }
  return (
    <Avatar
      sx={{
        bgcolor: 'transparent',
        border: '1px dashed',
        borderColor: 'text.disabled',
        color: 'text.disabled',
        fontSize: 12,
      }}
    >
      ?
    </Avatar>
  )
}

const RecurrenceOptions = ({ form, repeat, setRepeat }) => {
  const start = moment(form.start)
  const lengthWeeks = (weeks) =>
    start.clone().add(weeks, 'weeks').subtract(1, 'day').startOf('day')

  const toggleDay = (value) =>
    setRepeat((prev) => {
      const days = new Set(prev.daysOfWeek ?? [])
      if (days.has(value)) days.delete(value)
      else days.add(value)
      return { ...prev, daysOfWeek: [...days].sort() }
    })

  return (
    <Stack spacing={2.5}>
      <Box>
        <SectionLabel>Repeat on</SectionLabel>
        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.5}>
          {repeatDayOptions.map((day) => {
            const checked = repeat.daysOfWeek?.includes(day.value)
            return (
              <Tooltip key={day.value} title={day.tooltip}>
                <Button
                  size="small"
                  variant={checked ? 'contained' : 'outlined'}
                  onClick={() => toggleDay(day.value)}
                  aria-pressed={checked}
                  aria-label={day.tooltip}
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    px: 0,
                    borderRadius: '50%',
                  }}
                >
                  {day.label}
                </Button>
              </Tooltip>
            )
          })}
        </Stack>
        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={1}>
          {DAY_PRESETS.map((preset) => (
            <Chip
              key={preset.label}
              size="small"
              label={preset.label}
              clickable
              color={
                sameDays(repeat.daysOfWeek, preset.days) ? 'primary' : 'default'
              }
              variant={
                sameDays(repeat.daysOfWeek, preset.days) ? 'filled' : 'outlined'
              }
              onClick={() =>
                setRepeat((prev) => ({
                  ...prev,
                  daysOfWeek: [...preset.days].sort(),
                }))
              }
            />
          ))}
        </Stack>
      </Box>

      <TextField
        select
        size="small"
        label="Frequency"
        value={repeat.intervalWeeks ?? 1}
        onChange={(e) =>
          setRepeat((prev) => ({
            ...prev,
            intervalWeeks: Number(e.target.value),
          }))
        }
        fullWidth
      >
        <MenuItem value={1}>Every week</MenuItem>
        <MenuItem value={2}>Every 2 weeks</MenuItem>
        <MenuItem value={3}>Every 3 weeks</MenuItem>
        <MenuItem value={4}>Every 4 weeks</MenuItem>
      </TextField>

      <Box>
        <SectionLabel>For how long</SectionLabel>
        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.5} mb={1.5}>
          {LENGTH_PRESETS.map((weeks) => {
            const selected = moment(repeat.until).isSame(
              lengthWeeks(weeks),
              'day',
            )
            return (
              <Chip
                key={weeks}
                size="small"
                clickable
                label={`${weeks} week${weeks === 1 ? '' : 's'}`}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() =>
                  setRepeat((prev) => ({ ...prev, until: lengthWeeks(weeks) }))
                }
              />
            )
          })}
        </Stack>
        <AnDatePicker
          label="Repeat until"
          date={repeat.until}
          openTo="day"
          minDate={start.clone().startOf('day')}
          onChange={(value) =>
            setRepeat((prev) => ({
              ...prev,
              until:
                value && moment(value).isValid()
                  ? moment(value).startOf('day')
                  : null,
            }))
          }
          size="small"
          fullWidth
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        Each duty copies the vehicle, base, times, crew seats and settings you
        have set up above. The run starts on{' '}
        <b>{start.isValid() ? start.format('ddd D MMM') : '…'}</b>; days that
        aren&apos;t selected are skipped.
      </Typography>
    </Stack>
  )
}

const OccurrenceRow = ({
  occurrence,
  occurrenceIndex,
  form,
  repeat,
  templates,
  expanded,
  onToggle,
  onSeatChange,
  issues,
  duplicateSeats,
}) => {
  const slots = templates.map((template) =>
    getSeatSlot(repeat, occurrence, occurrenceIndex, template),
  )
  const assignedCount = slots.filter(isSlotAssigned).length
  const generalIssues = (issues ?? []).filter((issue) => issue.slot == null)
  const needsAttention = (issues?.length ?? 0) > 0 || !!duplicateSeats?.length

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, open) => onToggle(open)}
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        border: 1,
        borderRadius: 2,
        boxShadow: 'none',
        borderColor: needsAttention
          ? 'error.main'
          : expanded
            ? 'primary.main'
            : 'divider',
        bgcolor: 'background.paper',
        '&:not(:last-child)': { mb: 1 },
        '&::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          px: 1.5,
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            my: 1,
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            flexShrink: 0,
            textAlign: 'center',
            borderRadius: 1.5,
            py: 0.25,
            bgcolor: 'background.default',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            lineHeight={1.3}
            fontWeight={700}
          >
            {occurrence.start.format('ddd').toUpperCase()}
          </Typography>
          <Typography fontWeight={800} lineHeight={1.1}>
            {occurrence.start.format('D')}
          </Typography>
        </Box>
        <Box minWidth={0} flex={1}>
          <Typography variant="body2" fontWeight={700} noWrap component="div">
            {occurrence.start.format('D MMMM YYYY')}
            {occurrenceIndex === 0 && (
              <Chip
                size="small"
                label="First"
                sx={{ ml: 1, height: 18, fontSize: 11 }}
              />
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {formatDutyRange(occurrence.start, occurrence.end)}
            {templates.length > 0 &&
              ` · ${assignedCount} of ${templates.length} crew assigned`}
          </Typography>
        </Box>
        {needsAttention ? (
          <Chip size="small" color="error" label="Needs attention" />
        ) : (
          templates.length > 0 && (
            <AvatarGroup
              max={4}
              sx={{
                '& .MuiAvatar-root': { width: 26, height: 26, fontSize: 12 },
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              {slots.map((slot, index) => (
                <SeatAvatar key={index} slot={slot} />
              ))}
            </AvatarGroup>
          )
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{ px: 1.5, pt: 0.5, pb: 1.75, bgcolor: 'background.default' }}
      >
        <Stack spacing={1.5}>
          {generalIssues.map((issue, index) => (
            <Alert key={index} severity="error">
              {issue.message}
            </Alert>
          ))}
          {!templates.length && (
            <Typography variant="body2" color="text.secondary">
              No crew seats on this duty.
            </Typography>
          )}
          {templates.map((template, templateIndex) => {
            const times = getSeatTimes(occurrence, template, form)
            const locked = isLockedSeat(occurrenceIndex, template)
            const seatIssues = [
              ...(issues ?? []).filter(
                (issue) => issue.slot === template.index,
              ),
              ...(duplicateSeats?.includes(template.index)
                ? [{ message: 'This person is already in another seat on this duty.' }]
                : []),
            ]
            return (
              <Box key={template.id}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  display="block"
                  mb={0.75}
                >
                  Seat {templateIndex + 1} ·{' '}
                  {template.shift?.clinicalRole || 'Any role'} ·{' '}
                  {formatDutyRange(times.start, times.end)}
                </Typography>
                {locked ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <LinkIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      Existing shift
                      {isSlotAssigned(slots[templateIndex]) &&
                        ` (${
                          slots[templateIndex].assignedToSubcontractor
                            ? 'external subcontractor'
                            : getAssigneeName(slots[templateIndex].assignedTo)
                        })`}{' '}
                      — linked to this duty as it is
                    </Typography>
                  </Stack>
                ) : (
                  <SlotAssignmentFields
                    slot={slots[templateIndex]}
                    dense
                    disabled={repeat.sameCrew}
                    issues={seatIssues}
                    onAssigneeChange={(e, value) =>
                      onSeatChange(occurrence.key, template.id, {
                        assignedTo: value ?? null,
                        assignedToSubcontractor: false,
                      })
                    }
                    onSubcontractorToggle={(checked) =>
                      onSeatChange(occurrence.key, template.id, {
                        assignedTo: null,
                        assignedToSubcontractor: checked,
                      })
                    }
                  />
                )}
              </Box>
            )
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

const RepeatSummary = ({
  form,
  repeat,
  setRepeat,
  occurrences,
  templates,
  issuesByOccurrence,
  duplicateSeats,
}) => {
  const [expanded, setExpanded] = useState(null)
  const overCap = occurrences.length > MAX_REPEAT_DUTIES
  const shiftCount = occurrences.length * templates.length
  const slotsByOccurrence = occurrences.map((occurrence, occurrenceIndex) =>
    templates.map((template) =>
      getSeatSlot(repeat, occurrence, occurrenceIndex, template),
    ),
  )
  const assignedCount = slotsByOccurrence
    .flat()
    .filter(isSlotAssigned).length
  const openCount = shiftCount - assignedCount
  const firstAssigned = templates.some((template) =>
    isSlotAssigned(template.defaultSlot),
  )

  // Open the first duty the server rejected so the reason is visible
  useEffect(() => {
    const flagged = Object.keys(issuesByOccurrence ?? {}).map(Number)
    if (flagged.length) setExpanded(Math.min(...flagged))
  }, [issuesByOccurrence])

  const setSeat = (occurrenceKey, templateId, slot) =>
    setRepeat((prev) => ({
      ...prev,
      assignments: {
        ...prev.assignments,
        [occurrenceKey]: {
          ...(prev.assignments?.[occurrenceKey] ?? {}),
          [templateId]: slot,
        },
      },
    }))

  // Turning "same crew" off keeps what each duty currently shows, so nothing
  // visibly changes until the user edits a duty
  const toggleSameCrew = (checked) =>
    setRepeat((prev) => {
      if (checked) return { ...prev, sameCrew: true }
      const assignments = {}
      occurrences.forEach((occurrence, occurrenceIndex) => {
        assignments[occurrence.key] = {}
        templates.forEach((template) => {
          assignments[occurrence.key][template.id] = getSeatSlot(
            prev,
            occurrence,
            occurrenceIndex,
            template,
          )
        })
      })
      return { ...prev, sameCrew: false, assignments }
    })

  const clearOthers = () =>
    setRepeat((prev) => {
      const assignments = {}
      occurrences.forEach((occurrence, occurrenceIndex) => {
        if (occurrenceIndex === 0) {
          assignments[occurrence.key] = prev.assignments?.[occurrence.key] ?? {}
          return
        }
        assignments[occurrence.key] = Object.fromEntries(
          templates.map((template) => [template.id, emptySlot()]),
        )
      })
      return { ...prev, assignments }
    })

  return (
    <Stack spacing={2} minWidth={0}>
      <Box>
        <SectionLabel>Summary</SectionLabel>
        <Stack direction="row" gap={1} mt={0.5}>
          <Stat
            value={overCap ? `${MAX_REPEAT_DUTIES}+` : occurrences.length}
            label={occurrences.length === 1 ? 'duty' : 'duties'}
          />
          <Stat
            value={overCap ? '—' : shiftCount}
            label={shiftCount === 1 ? 'crew shift' : 'crew shifts'}
          />
          <Stat
            value={overCap ? '—' : assignedCount}
            label="assigned"
            tone={assignedCount ? 'primary.main' : undefined}
          />
          <Stat
            value={overCap ? '—' : openCount}
            label={openCount === 1 ? 'open vacancy' : 'open vacancies'}
            tone={openCount ? 'warning.main' : undefined}
          />
        </Stack>
        {occurrences.length > 0 && !overCap && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {occurrences[0].start.format('ddd D MMM')} –{' '}
            {occurrences[occurrences.length - 1].start.format('ddd D MMM YYYY')}
            {openCount > 0 && ' · open vacancies are advertised to staff'}
          </Typography>
        )}
      </Box>

      {overCap ? (
        <Alert severity="error">
          That pattern makes more than {MAX_REPEAT_DUTIES} duties. Shorten the
          run or pick fewer days.
        </Alert>
      ) : shiftCount > MAX_REPEAT_SHIFTS ? (
        <Alert severity="error">
          That would create {shiftCount} crew shifts — the most one repeat can
          create is {MAX_REPEAT_SHIFTS}. Shorten the run or pick fewer days.
        </Alert>
      ) : !occurrences.length ? (
        <Alert severity="warning">
          Choose at least one day and an end date on or after the duty&apos;s
          start.
        </Alert>
      ) : null}

      {!templates.length ? (
        <Alert severity="info">
          No crew seats yet. Add crew shifts above and they&apos;ll be repeated
          on every duty — or create the duties now and crew them later.
        </Alert>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            borderColor: repeat.sameCrew ? 'primary.main' : 'divider',
          }}
        >
          <FormControlLabel
            sx={{ mx: 0, alignItems: 'flex-start' }}
            control={
              <Switch
                checked={!!repeat.sameCrew}
                onChange={(e) => toggleSameCrew(e.target.checked)}
                size="small"
              />
            }
            label={
              <Box ml={0.5}>
                <Typography variant="body2" fontWeight={700}>
                  Assign the same crew to every duty
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {repeat.sameCrew
                    ? firstAssigned
                      ? 'Each duty gets the crew you picked above. Turn off to assign duties one by one.'
                      : 'No one is assigned above yet, so every seat will be an open vacancy. Turn off to assign duties one by one.'
                    : 'Assign each duty below. Seats left empty are advertised as open vacancies.'}
                </Typography>
              </Box>
            }
          />
          {!repeat.sameCrew && occurrences.length > 1 && (
            <Box textAlign="right">
              <Button size="small" color="inherit" onClick={clearOthers}>
                Clear all but the first duty
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {occurrences.length > 0 && !overCap && (
        <Box
          sx={{
            maxHeight: { lg: 460 },
            overflowY: { lg: 'auto' },
            pr: { lg: 0.5 },
          }}
        >
          {occurrences.map((occurrence, occurrenceIndex) => (
            <OccurrenceRow
              key={occurrence.key}
              occurrence={occurrence}
              occurrenceIndex={occurrenceIndex}
              form={form}
              repeat={repeat}
              templates={templates}
              expanded={expanded === occurrenceIndex}
              onToggle={(open) => setExpanded(open ? occurrenceIndex : null)}
              onSeatChange={setSeat}
              issues={issuesByOccurrence?.[occurrenceIndex]}
              duplicateSeats={duplicateSeats?.[occurrenceIndex]}
            />
          ))}
        </Box>
      )}
    </Stack>
  )
}

// Recurrence options on the left, what will be created (and who's on it) on
// the right; stacked below lg.
const RepeatDutyPanel = (props) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
    {/* Box keeps the Grid gutter intact inside the form's Stack/Grid */}
    <Box>
      <Grid container>
        <Grid
          item
          xs={12}
          lg={5}
          sx={{
            p: 2,
            bgcolor: 'background.default',
            borderRight: { lg: 1 },
            borderBottom: { xs: 1, lg: 0 },
            borderColor: { xs: 'divider', lg: 'divider' },
          }}
        >
          <RecurrenceOptions
            form={props.form}
            repeat={props.repeat}
            setRepeat={props.setRepeat}
          />
        </Grid>
        <Grid item xs={12} lg={7} sx={{ p: 2, minWidth: 0 }}>
          <RepeatSummary {...props} />
        </Grid>
      </Grid>
    </Box>
  </Paper>
)

export default RepeatDutyPanel
